<template>
  <div :class="['nixia-message-bubble-row', message.from]">
    <div v-if="message.from === 'nixia'" class="nixia-message-bubble-avatar nixia-avatar">
      <img src="https://espaco.blob.core.windows.net/nixloc-imagens/logo_ia.png" alt="Nixia" class="nixia-message-bubble-avatar-img" />
    </div>

    <div class="nixia-message-bubble-content">
      <div class="nixia-message-bubble" :class="{ 'nixia-message-bubble-error': message.error }">
        <div
          v-if="message.from === 'nixia'"
          class="nixia-message-bubble-markdown"
          v-html="renderedText"
          @click="onContentClick"
        ></div>
        <template v-else>{{ message.text }}</template>
      </div>

      <div v-if="message.attachments && message.attachments.length" class="nixia-message-bubble-attachments">
        <template v-for="(attachment, index) in message.attachments">
          <NixiaAttachmentDownload
            v-if="attachment.content"
            :key="attachment.fileName || index"
            :attachment="attachment"
          />
          <a
            v-else
            :key="attachment.id || attachment.url || index"
            :href="attachment.url"
            target="_blank"
            rel="noopener"
            class="nixia-message-bubble-attachment"
          >
            <i class="fa-solid fa-paperclip"></i>
            <span>{{ attachment.name }}</span>
          </a>
        </template>
      </div>
    </div>

    <div v-if="message.from === 'user'" class="nixia-message-bubble-avatar user-avatar">
      <img :src="userPhotoUrl" alt="Você" class="nixia-message-bubble-avatar-img" />
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import { renderMarkdown } from "../utils/markdown";
import NixiaAttachmentDownload from "./NixiaAttachmentDownload.vue";

export default {
  name: "NixiaMessageBubble",
  components: { NixiaAttachmentDownload },
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState("user", ["userLogged"]),
    userPhotoUrl() {
      const photo = this.userLogged && this.userLogged.user && this.userLogged.user.photo;
      return `https://espaco.blob.core.windows.net/nixloc-photo-user/${photo || "noImageEdit.png"}`;
    },
    renderedText() {
      return this.message.from === "nixia" ? renderMarkdown(this.message.text) : "";
    },
  },
  methods: {
    ...mapMutations("generic", ["openModal"]),
    ...mapMutations("nixia", ["setVideoPreviewUrl"]),
    onContentClick(event) {
      const link = event.target.closest(".nixia-video-link");
      if (!link) return;

      event.preventDefault();
      this.setVideoPreviewUrl(link.getAttribute("data-embed-url"));
      this.openModal("nixiaVideoPreview");
    },
  },
};
</script>

<style scoped>
.nixia-message-bubble-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.nixia-message-bubble-row.user {
  flex-direction: row-reverse;
}

.nixia-message-bubble-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
}

.nixia-avatar {
  background: transparent;
  color: var(--text-inverse);
  overflow: hidden;
}

.nixia-message-bubble-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-avatar {
  background: var(--bg-elevated);
  overflow: hidden;
}

.nixia-message-bubble-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 72%;
}

.nixia-message-bubble-row.user .nixia-message-bubble-content {
  align-items: flex-end;
}

.nixia-message-bubble {
  padding: 12px 16px;
  border-radius: 14px;
  font-size: 14.5px;
  line-height: 1.6;
  white-space: pre-wrap;
}

.nixia-message-bubble-row.nixia .nixia-message-bubble {
  background: var(--bg-elevated);
  color: var(--text-primary);
  border-bottom-left-radius: 4px;
}

.nixia-message-bubble-row.user .nixia-message-bubble {
  background: var(--color-brand);
  color: var(--color-brand-contrast);
  border-bottom-right-radius: 4px;
}

.nixia-message-bubble-error {
  border: 1px solid var(--color-danger);
}

.nixia-message-bubble-markdown {
  white-space: normal;
}

.nixia-message-bubble-markdown ::v-deep p {
  margin: 0 0 8px;
}

.nixia-message-bubble-markdown ::v-deep p:last-child {
  margin-bottom: 0;
}

.nixia-message-bubble-markdown ::v-deep ul,
.nixia-message-bubble-markdown ::v-deep ol {
  margin: 0 0 8px;
  padding-left: 20px;
}

.nixia-message-bubble-markdown ::v-deep li {
  margin-bottom: 2px;
}

.nixia-message-bubble-markdown ::v-deep h1,
.nixia-message-bubble-markdown ::v-deep h2,
.nixia-message-bubble-markdown ::v-deep h3,
.nixia-message-bubble-markdown ::v-deep h4 {
  margin: 12px 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.nixia-message-bubble-markdown ::v-deep h1:first-child,
.nixia-message-bubble-markdown ::v-deep h2:first-child,
.nixia-message-bubble-markdown ::v-deep h3:first-child,
.nixia-message-bubble-markdown ::v-deep h4:first-child {
  margin-top: 0;
}

.nixia-message-bubble-markdown ::v-deep strong {
  font-weight: 600;
}

.nixia-message-bubble-markdown ::v-deep code {
  background: var(--bg-hover);
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 13px;
}

.nixia-message-bubble-markdown ::v-deep pre {
  background: var(--bg-hover);
  padding: 10px 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 0 0 8px;
}

.nixia-message-bubble-markdown ::v-deep pre code {
  background: none;
  padding: 0;
}

.nixia-message-bubble-markdown ::v-deep a {
  color: var(--color-brand);
  text-decoration: underline;
}

.nixia-message-bubble-markdown ::v-deep a.nixia-video-link {
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.nixia-message-bubble-attachments {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nixia-message-bubble-attachment {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-base);
  color: var(--color-brand);
  font-size: 13px;
  text-decoration: none;
}

.nixia-message-bubble-attachment:hover {
  background: var(--bg-hover);
}
</style>
